/* @flow weak */
import invariant from 'invariant';
import * as React from 'react';
import BoxBody from 'components/BoxBody.jsx';
import Input from 'components/Input.jsx';
import Link from 'components/Link.jsx';
import FormElement from 'components/FormElement.jsx';
import BoxHeader from 'components/BoxHeader.jsx';
import ToastActions from 'actions/ToastActions';
import DeviceActions from 'actions/DeviceActions';
import Button from 'components/Button.jsx';
import BoxFooter from 'components/BoxFooter.jsx';
import assertions from 'lib/assertions';
import { formatAsPhoneNumber, ensureLeading1, stripSpaces } from 'lib/phoneNumbers';
import { pickBy, identity } from 'lodash';

import './styles/LinkSmsVerificationCodeBox.scss';

type Props = {
  token?: ?string,
  onResend: Function,
  // This will replace calling the standard /v2/devices/sms/outbound_verification call
  onSubmit?: string => void,
  // This gets called after using the normal device verification call if onSubmit isn't provided
  onDone?: Function,
  phoneNumber?: ?string,
  footer?: ?React.Element<any>,
  successMessage?: string,
  /* Skip updating device preferences:
   *
   * When adding a new device the backend
   * try to make smart choice about enabling the added device
   * and disabeling preixisting one. This skips it, the added
   * device will be disabled, and current devices will not change.
   */
  skipUpdateDevicePreferences?: boolean,
};

type State = {
  submitDisabled: boolean,
  submitCode: string,
};

class LinkSmsVerificationCodeBox extends React.Component<Props, State> {
  state: State = {
    submitDisabled: true,
    submitCode: '',
  };

  handleValidation = e => {
    assertions.lengthEqualTo(stripSpaces(e.target.value), 4);
  };

  handleValidChange = e => {
    this.setState({ submitDisabled: false, submitCode: stripSpaces(e.target.value) });
  };

  handleInvalidChange = () => {
    this.setState({ submitDisabled: true, submitCode: '' });
  };

  onResend = () => {
    this.setState({ submitDisabled: true, submitCode: '' });
    this.props.onResend();
  };

  submit = () => {
    if (this.props.onSubmit) {
      this.props.onSubmit(this.state.submitCode);
    } else {
      const onSuccess = device => {
        if (this.props.onDone) {
          this.props.onDone(device);
        }
        ToastActions.showSuccessToast(this.props.successMessage || 'Mobile phone linked to Remind');
      };

      const onError = () => {
        ToastActions.showErrorToast('Please enter a valid code.');
      };

      const options = pickBy(
        {
          skipUpdateDevicePreferences: this.props.skipUpdateDevicePreferences,
        },
        identity
      );

      invariant(this.props.token, 'LinkSmsVerificationCodeBox submit without token');
      DeviceActions.createOutboundDevice(this.props.token, this.state.submitCode, options).then(
        onSuccess,
        onError
      );
    }
  };

  render() {
    const formattedPhoneNumber =
      this.props.phoneNumber && formatAsPhoneNumber(ensureLeading1(this.props.phoneNumber));
    const subtext = formattedPhoneNumber
      ? `Please enter the 4-digit code sent to ${formattedPhoneNumber}.`
      : 'Please enter the 4-digit code sent to your phone.';

    return (
      <div>
        <BoxHeader title="Enter your code" subtext={subtext} />
        <BoxBody divided>
          <div className="LinkSmsVerificationCodeBox">
            <FormElement
              autofocus
              label="Verification code"
              labelHidden
              validate={this.handleValidation}
              onValidChange={this.handleValidChange}
              onInvalidChange={this.handleInvalidChange}
              element={Input}
              elementProps={{
                className: 'LinkSmsVerificationCodeBox-Input',
                name: 'code',
                size: 'large',
                type: 'text',
              }}
            />
            <Button
              className="LinkSmsVerificationCodeBox-Submit"
              eventElementName="LinkSmsVerificationCodeBox-Submit"
              onClick={this.submit}
              type="submit"
              disabled={this.state.submitDisabled}>
              Submit
            </Button>
          </div>
        </BoxBody>
        <BoxFooter>
          Didn’t receive a code?{' '}
          <Link data-test="resend-link" onClick={this.onResend}>
            Resend
          </Link>
          {this.props.footer ? ' ' : null}
          {this.props.footer}
        </BoxFooter>
      </div>
    );
  }
}
export default LinkSmsVerificationCodeBox;
